import { Feature, Plan } from './types';

interface FeatureConfig {
    title: string;
    href?: string;
    tooltip?: string;
}

interface PlanConfig {
    title: string;
    price: {
        monthly: number | string;
        annually: number | string;
    };
    credits: number | string;
}

type FeatureValues = Record<Feature, boolean>;

export const featureConfigs: Record<Feature, FeatureConfig> = {
    [Feature.DATABASE_EXPLORER]: {
        title: 'Vulnerability database explorer',
        href: 'https://docs.vulners.com/docs/api/search/'
    },
    [Feature.DATABASE_API]: {
        title: 'Database API *',
        href: 'https://docs.vulners.com/docs/api/'
    },
    [Feature.VULNERABILITY_ASSESSMENT]: {
        title: 'Vulnerability Assessment *',
        href: 'https://docs.vulners.com/docs/api/audit/'
    },
    [Feature.ALERTS]: {
        title: 'Alerts *',
        href: 'https://docs.vulners.com/docs/products/alerts/'
    },
    [Feature.VULNERS_MCP]: {
        title: 'Vulners MCP *',
        href: 'https://docs.vulners.com/docs/products/mcp/'
    },
    [Feature.DATASET_EXPORT]: {
        title: 'Dataset export for off-line usage *',
        href: 'https://docs.vulners.com/docs/api/collections/'
    },
    [Feature.CVE_FUSION]: {
        title: 'CVE Fusion',
        tooltip:
            'Access to a synthetic collection that aggregates CVE data from CVE List, NVD, and Vulnrichment and also includes Vulners enrichments like custom CPE configurations and direct links to exploits.'
    },
    [Feature.VULNERS_EXPLOITS]: {
        title: 'Vulners Exploits',
        href: 'https://docs.vulners.com/docs/products/exploits/'
    },
    [Feature.PARTNER_INTEGRATION_SUITE]: {
        title: 'Partner Integration Suite',
        tooltip: 'OEM license option to integrate Vulners data into your products.'
    }
};

export const planConfigs: Record<Plan, PlanConfig> = {
    [Plan.FREE]: {
        title: 'Free',
        price: { monthly: 'Free', annually: 'Free' },
        credits: 100
    },
    [Plan.BASIC]: {
        title: 'Basic',
        price: { monthly: 600, annually: 475 },
        credits: 600
    },
    [Plan.PRO]: {
        title: 'Pro',
        price: { monthly: 1300, annually: 1075 },
        credits: 3000
    },
    [Plan.OEM]: {
        title: 'OEM',
        price: { monthly: 0, annually: 0 },
        credits: 'Custom'
    }
};

export const planFeatures: Record<Plan, FeatureValues> = {
    [Plan.FREE]: {
        [Feature.DATABASE_EXPLORER]: true,
        [Feature.DATABASE_API]: true,
        [Feature.VULNERABILITY_ASSESSMENT]: true,
        [Feature.ALERTS]: true,
        [Feature.VULNERS_MCP]: true,
        [Feature.DATASET_EXPORT]: false,
        [Feature.CVE_FUSION]: false,
        [Feature.VULNERS_EXPLOITS]: false,
        [Feature.PARTNER_INTEGRATION_SUITE]: false
    },
    [Plan.BASIC]: {
        [Feature.DATABASE_EXPLORER]: true,
        [Feature.DATABASE_API]: true,
        [Feature.VULNERABILITY_ASSESSMENT]: true,
        [Feature.ALERTS]: true,
        [Feature.VULNERS_MCP]: true,
        [Feature.DATASET_EXPORT]: true,
        [Feature.CVE_FUSION]: true,
        [Feature.VULNERS_EXPLOITS]: false,
        [Feature.PARTNER_INTEGRATION_SUITE]: false
    },
    [Plan.PRO]: {
        [Feature.DATABASE_EXPLORER]: true,
        [Feature.DATABASE_API]: true,
        [Feature.VULNERABILITY_ASSESSMENT]: true,
        [Feature.ALERTS]: true,
        [Feature.VULNERS_MCP]: true,
        [Feature.DATASET_EXPORT]: true,
        [Feature.CVE_FUSION]: true,
        [Feature.VULNERS_EXPLOITS]: true,
        [Feature.PARTNER_INTEGRATION_SUITE]: false
    },
    [Plan.OEM]: {
        [Feature.DATABASE_EXPLORER]: true,
        [Feature.DATABASE_API]: true,
        [Feature.VULNERABILITY_ASSESSMENT]: true,
        [Feature.ALERTS]: true,
        [Feature.VULNERS_MCP]: true,
        [Feature.DATASET_EXPORT]: true,
        [Feature.CVE_FUSION]: true,
        [Feature.VULNERS_EXPLOITS]: true,
        [Feature.PARTNER_INTEGRATION_SUITE]: true
    }
};

export const plans = [Plan.FREE, Plan.BASIC, Plan.PRO, Plan.OEM] as const;
export const features = [
    Feature.DATABASE_EXPLORER,
    Feature.DATABASE_API,
    Feature.VULNERABILITY_ASSESSMENT,
    Feature.ALERTS,
    Feature.VULNERS_MCP,
    Feature.DATASET_EXPORT,
    Feature.CVE_FUSION,
    Feature.VULNERS_EXPLOITS,
    Feature.PARTNER_INTEGRATION_SUITE
] as const;
